/**
 * API client — fetch na isti origin (LIVE: Express streze panel + `/api` iz `apps/web/dist`).
 * Opcijsko pri `npm run dev:web` Vite proxy usmeri `/api` na localhost:5000 (glej vite.config.ts).
 */

export class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(status: number, message: string, data: unknown) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(init.headers || {}),
    },
    ...init,
  });

  const contentType = res.headers.get("content-type") || "";
  const body = contentType.includes("application/json")
    ? await res.json().catch(() => ({}))
    : await res.text();

  if (!res.ok) {
    const msg =
      (typeof body === "object" &&
        body &&
        (body as { error?: string }).error) ||
      res.statusText ||
      "API napaka";
    throw new ApiError(res.status, msg, body);
  }

  return body as T;
}

// ---------- Types ----------

export interface CurrentUser {
  id: string;
  username: string;
  avatar: string | null;
  is_dashboard_owner: boolean;
  is_beta_tester?: boolean;
  /** Debug help: your ID (string), whether you are in the owner set, how many the server loaded */
  your_discord_id_normalized?: string;
  is_in_owner_list?: boolean;
  dashboard_owners_total?: number;
}

export interface PublicStats {
  guild_count: number;
  commands_approx: number;
  uptime_display: string;
  tickets_line: string | null;
  trusted_line: string;
  /** Permanent invite to host community/support Discord (from COMMUNITY_DISCORD_URL). */
  community_discord_url?: string | null;
}

export interface Guild {
  id: string;
  name: string;
  icon: string | null;
  icon_url: string | null;
  owner: boolean;
  bot_in_guild: boolean;
  is_premium: boolean;
  invite_url: string;
  /** Who may open /premium and checkout (owner, OWNER_IDS, or delegated roles). */
  can_buy_premium?: boolean;
}

export interface DiscordChannel {
  id: string;
  name: string;
  type: number;
  position?: number;
  parent_id?: string | null;
}

export interface DiscordRole {
  id: string;
  name: string;
  color: number;
  position: number;
}

export interface ServerPanelData {
  guild: Guild;
  config: GuildConfig;
  premium: boolean;
  is_guild_owner: boolean;
  can_buy_premium: boolean;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  security_manager?: boolean;
}

/** One modal text-input question (used by ticket intake forms and whitelist applications). */
export interface TicketQuestion {
  id: string;
  label: string;
  style: "short" | "paragraph";
  required: boolean;
  max_length: number;
  placeholder?: string;
}

/** Full guild config schema (mirrors bot_integration.default_cfg). */
export interface GuildConfig {
  tickets: {
    enabled: boolean;
    panel_channel_id: string;
    transcript_channel_id: string;
    panel_mode?: "select" | "buttons" | "reactions" | "sections";
    dm_user_on_open?: boolean;
    panel_title: string;
    panel_description: string;
    panel_image_url?: string;
    panel_image_position?: "none" | "thumbnail" | "image";
    panel_footer_text?: string;
    panel_categories_heading?: string;
    admin_role_ids: string[];
    staff_role_ids: string[];
    dev_role_ids: string[];
    manager_role_ids: string[];
    categories: Array<{
      key: string;
      label: string;
      emoji: string;
      description: string;
      ticket_message?: string;
      category_id: string;
      ping_role_ids: string[];
      /**
       * Optional intake form shown before the ticket is created — answers become the
       * first message in the ticket.
       * "direct" (default): one modal built from `questions`.
       * "choice": user first picks from a dropdown (`option_groups`), then gets a modal
       *   built from that option's own `questions` — e.g. "Paysafe → PayPal" vs
       *   "PayPal → Paysafe" each asking for different fields.
       * Every question list is capped at 5 (Discord modal input limit).
       */
      question_mode?: "direct" | "choice";
      questions?: TicketQuestion[];
      option_groups?: Array<{
        id: string;
        label: string;
        description?: string;
        questions: TicketQuestion[];
      }>;
    }>;
    auto_close?: {
      enabled: boolean;
      inactivity_hours: number;
      close_after_hours: number;
    };
    ai_assistant?: {
      enabled: boolean;
      knowledge_base: string;
      system_prompt: string;
      modal_title: string;
      modal_label: string;
      auto_reply?: boolean;
    };
    panel_message_id?: string;
  };
  whitelist: {
    enabled: boolean;
    channel_id: string;
    result_channel_id: string;
    notification_channel_id?: string;
    /** Panel embed/section title + description (e.g. instructions in 2 languages). Defaults used if blank. */
    panel_title?: string;
    panel_description?: string;
    custom_questions_enabled?: boolean;
    questions?: Array<{
      id: string;
      label: string;
      style: "short" | "paragraph";
      required: boolean;
      max_length: number;
      placeholder?: string;
    }>;
    /**
     * "multi" shows one panel button per entry in `language_variants` (e.g. "Start Whitelist
     * SLO" / "Start Whitelist EN"), each opening its own question set. Kept as an explicit
     * mode (not inferred from array length) so removing a variant down to 0 or 1 while
     * editing doesn't silently kick the admin back to single-language mode.
     */
    language_mode?: "single" | "multi";
    language_variants?: Array<{
      id: string;
      button_label: string;
      questions: TicketQuestion[];
    }>;
    submit_message?: string;
    accept_message?: string;
    reject_message?: string;
    dm_result?: boolean;
    cooldown_hours?: number;
    admin_role_ids: string[];
    accepted_role_id: string;
    panel_message_id?: string;
    /** buttons = Message Components; reactions = emoji; sections = Components v2 layout */
    panel_mode?: "buttons" | "reactions" | "sections";
    panel_reaction_emoji?: string;
  };
  blacklist: {
    enabled: boolean;
    role_id: string;
    member_role_id?: string;
    log_channel_id: string;
    mod_role_ids: string[];
    admin_role_ids: string[];
    /** Per-server blacklist rows (managed by /blacklist; preserved on panel save if omitted) */
    entries?: Array<{ id: string; tag: string; added_by: string; reason: string; timestamp: number }>;
  };
  daily: {
    enabled: boolean;
    channel_id: string;
    messages: string[];
    hour: number;
    minute: number;
    random_pick: boolean;
  };
  custom_commands: {
    enabled: boolean;
    prefix: string;
    log_channel_id: string;
    commands: Array<{
      id: string;
      enabled: boolean;
      trigger: string;
      response: string;
      match_mode: "exact" | "starts_with" | "contains";
      reply_mode: "reply" | "channel";
      delete_trigger: boolean;
      cooldown_seconds: number;
      allowed_channel_ids: string[];
      allowed_role_ids: string[];
      /** Off by default — blocks @everyone/@here/role/user pings in the response from
       *  actually notifying anyone, even if typed literally, to prevent spam abuse. */
      allow_mentions?: boolean;
    }>;
    auto_responses: Array<{
      id: string;
      enabled: boolean;
      name: string;
      keywords: string[];
      response: string;
      match_mode: "contains" | "word" | "exact";
      reply_mode: "reply" | "channel";
      delete_trigger: boolean;
      cooldown_seconds: number;
      allowed_channel_ids: string[];
      ignored_channel_ids: string[];
      allow_mentions?: boolean;
    }>;
  };
  stat_channels: {
    enabled: boolean;
    update_minutes: number;
    channels: Array<{
      id: string;
      enabled: boolean;
      channel_id: string;
      stat_type: "members" | "humans" | "bots" | "boosts" | "roles";
      name_template: string;
      role_id: string;
    }>;
  };
  bot_personalizer: {
    enabled: boolean;
    nickname: string;
    embed_author_name: string;
    embed_footer: string;
    embed_color: string;
    status_text: string;
    keep_orbit_branding: boolean;
  };
  social_alerts: {
    enabled: boolean;
    check_minutes: number;
    alerts: Array<{
      id: string;
      enabled: boolean;
      name: string;
      platform: "rss" | "youtube" | "reddit" | "twitch" | "kick" | "x" | "instagram" | "tiktok" | "other";
      feed_url: string;
      channel_id: string;
      message: string;
      embed_color: string;
      mention_role_ids: string[];
    }>;
  };
  music: {
    enabled: boolean;
    orbit_prefix: string;
    /** slash | prefix | both */
    orbit_command_mode: "slash" | "prefix" | "both";
    allowed_channel_ids: string[];
    dj_role_ids: string[];
    max_queue: number;
    default_volume: number;
  };
  reaction_roles: {
    enabled: boolean;
    panels: Array<{
      id: string;
      channel_id: string;
      title: string;
      description: string;
      color: string;
      mode: "toggle" | "unique";
      /** buttons | reactions | sections (v2) */
      display_style?: "buttons" | "reactions" | "sections";
      message_id?: string;
      buttons: Array<{
        idx: string;
        label: string;
        emoji: string;
        role_id: string;
        style: "primary" | "secondary" | "success" | "danger";
      }>;
    }>;
  };
  suggestions: {
    enabled: boolean;
    channel_id: string;
    approve_role_ids: string[];
    use_threads: boolean;
    allow_anonymous: boolean;
    auto_react: boolean;
  };
  polls: {
    enabled: boolean;
    allowed_channel_ids: string[];
    manager_role_ids: string[];
    default_duration_hours: number;
    max_options: number;
    allow_multiple_default: boolean;
    anonymous_default: boolean;
    show_live_results: boolean;
    show_poll_duration?: boolean;
    embed_color: string;
    log_channel_id: string;
  };
  ticket_feedback: {
    enabled: boolean;
    channel_id: string;
    dm_user: boolean;
  };
  welcome: {
    enabled: boolean;
    channel_id: string;
    title: string;
    message: string;
    color: string;
    show_avatar: boolean;
    ping_user: boolean;
  };
  goodbye: {
    enabled: boolean;
    channel_id: string;
    message: string;
    color: string;
  };
  autorole: {
    enabled: boolean;
    role_ids: string[];
  };
  moderation: {
    enabled: boolean;
    log_channel_id: string;
    mod_role_ids: string[];
    dm_on_action: boolean;
  };
  logging: {
    enabled: boolean;
    default_channel_id: string;
    message_channel_id: string;
    member_channel_id: string;
    moderation_channel_id: string;
    server_channel_id: string;
    voice_channel_id: string;
    log_message_delete: boolean;
    log_message_delete_channel_id?: string;
    log_message_edit: boolean;
    log_message_edit_channel_id?: string;
    log_member_join: boolean;
    log_member_join_channel_id?: string;
    /** When welcome is enabled, skip duplicate "Member joined" logs (default true). */
    suppress_member_join_when_welcome_enabled?: boolean;
    log_member_leave: boolean;
    log_member_leave_channel_id?: string;
    log_member_update: boolean;
    log_member_update_channel_id?: string;
    log_ban: boolean;
    log_ban_channel_id?: string;
    log_unban: boolean;
    log_unban_channel_id?: string;
    log_role_create: boolean;
    log_role_create_channel_id?: string;
    log_role_delete: boolean;
    log_role_delete_channel_id?: string;
    log_role_update: boolean;
    log_role_update_channel_id?: string;
    log_channel_create: boolean;
    log_channel_create_channel_id?: string;
    log_channel_delete: boolean;
    log_channel_delete_channel_id?: string;
    log_channel_update: boolean;
    log_channel_update_channel_id?: string;
    log_voice: boolean;
    ignore_bot_messages: boolean;
    ignored_channel_ids: string[];
  };
  /** Premium: per-guild text overrides for bot-facing strings (titles, labels, buttons). */
  language: {
    enabled: boolean;
    /** Sparse map: only overridden keys are stored, everything else falls back to English. */
    overrides: Record<string, string>;
  };
  voice_rewards: {
    enabled: boolean;
    log_channel_id: string;
    ignored_channel_ids: string[];
    tiers: Array<{
      id: string;
      enabled: boolean;
      label: string;
      hours: number;
      role_ids: string[];
      channel_ids: string[];
    }>;
  };
  leveling: {
    enabled: boolean;
    xp_min: number;
    xp_max: number;
    cooldown_seconds: number;
    level_up_message_enabled: boolean;
    level_up_channel_id: string;
    level_up_message: string;
    ignored_channel_ids: string[];
    ignored_role_ids: string[];
    level_roles: Array<{ level: number; role_id: string }>;
  };
  giveaways: {
    enabled: boolean;
    host_role_ids: string[];
    log_channel_id: string;
    /** buttons | reactions | sections (v2 layout + join button) */
    join_style?: "buttons" | "reactions" | "sections";
    reaction_emoji?: string;
  };
  games: {
    enabled: boolean;
    currency_name: string;
    /** Classic embeds vs Components v2 Container (sections) for minigame UIs */
    ui_style?: "embed" | "sections";
    allowed_channel_ids: string[];
    roulette: { enabled: boolean; min_bet: number; max_bet: number };
    slots: { enabled: boolean; min_bet: number; max_bet: number };
    coinflip: { enabled: boolean; min_bet: number; max_bet: number };
    blackjack: { enabled: boolean; min_bet: number; max_bet: number };
    tictactoe: { enabled: boolean; min_bet: number; max_bet: number };
    battleship: { enabled: boolean; min_bet: number; max_bet: number };
    memory: { enabled: boolean; min_bet: number; max_bet: number };
    mines: { enabled: boolean; min_bet: number; max_bet: number };
    dice: { enabled: boolean; min_bet: number; max_bet: number };
    duck: { enabled: boolean; min_bet: number; max_bet: number };
    hangman: { enabled: boolean; min_bet: number; max_bet: number; word_language: "english" | "slovenian" | "mixed" };
    horseracing: { enabled: boolean; min_bet: number; max_bet: number };
    game_daily: { enabled: boolean; amount: number };
    leaderboard: {
      enabled: boolean;
      channel_id: string;
      thread_id: string;
      message_id: string;
      embed_color: string;
      top_n: number;
    };
    promotion_enabled?: boolean;
    promotion_channel_id?: string;
    promotion_interval?: number;
  };
  community_modules: {
    dev_notes: string;
    roadmap_mirrors_ideas: boolean;
  };
  fivem: {
    enabled: boolean;
    server_name: string;
    connect_url: string;
    rules_channel_id: string;
    wl_channel_id: string;
    status_channel_id: string;
    status_message: string;
    auto_reply_enabled: boolean;
    auto_reply_keywords: string[];
    auto_reply_message: string;
  };
  webhooks: {
    enabled: boolean;
    jobs: Array<{
      id: string;
      enabled: boolean;
      name: string;
      url: string;
      channel_id: string;
      content: string;
      embed_title: string;
      embed_description: string;
      interval_minutes: number;
    }>;
  };
  sql_backup: {
    enabled: boolean;
    db_type: "mysql";
    host: string;
    port: number;
    database: string;
    username: string;
    password: string;
    webhook_url: string;
    interval_hours: number;
    retention: number;
    log_channel_id: string;
  };
  security: {
    enabled: boolean;
    log_channel_id: string;
    manager_role_ids: string[];
    exempt_role_ids: string[];
    exempt_channel_ids: string[];
    anti_spam: {
      enabled: boolean;
      max_messages: number;
      interval_seconds: number;
      action: "none" | "delete" | "warn" | "timeout" | "kick" | "ban";
      timeout_minutes: number;
      delete_messages: boolean;
      duplicate_enabled: boolean;
      duplicate_threshold: number;
      mention_limit: number;
      caps_enabled: boolean;
      caps_percent: number;
      caps_min_length: number;
    };
    anti_invite: {
      enabled: boolean;
      action: "none" | "delete" | "warn" | "timeout" | "kick" | "ban";
      allow_own_invites: boolean;
      delete_message: boolean;
    };
    automod: {
      enabled: boolean;
      blocked_words: string[];
      blocked_patterns: string[];
      match_mode: "contains" | "word";
      case_sensitive: boolean;
      delete_message: boolean;
      action: "none" | "delete" | "warn" | "timeout" | "kick" | "ban";
      timeout_minutes: number;
      dm_user: boolean;
    };
    anti_nuke: {
      enabled: boolean;
      window_seconds: number;
      max_channel_deletes: number;
      max_role_deletes: number;
      max_bans: number;
      action: "none" | "kick" | "ban";
    };
    anti_raid: {
      enabled: boolean;
      window_seconds: number;
      max_joins: number;
      cooldown_seconds: number;
      action: "alert" | "timeout_burst" | "kick_burst";
      timeout_minutes: number;
      min_account_age_minutes: number;
    };
    verification: {
      enabled: boolean;
      unverified_role_id: string;
      verified_role_id: string;
      skip_autorole_until_verified: boolean;
    };
    anti_link?: {
      enabled: boolean;
      action: "none" | "delete" | "warn" | "timeout" | "kick" | "ban";
      whitelisted_domains: string[];
    };
    anti_phishing?: boolean;
    anti_phishing_action?: "none" | "delete" | "warn" | "timeout" | "kick" | "ban";
    trap_channel: {
      enabled: boolean;
      channel_ids: string[];
      timeout_minutes: number;
    };
  };
  backup: {
    enabled: boolean;
    log_channel_id: string;
    create_role_ids: string[];
  };
  /** Roles allowed to purchase Premium besides the guild owner (owner + OWNER_IDS configure this). */
  dashboard: {
    premium_delegate_role_ids: string[];
  };
  ai_architect: {
    enabled: boolean;
  };
  ai_faq?: {
    enabled: boolean;
    knowledge_base: string;
    allowed_channel_ids: string[];
    trigger_mode: "mention" | "all" | "questions";
    reply_style: "embed" | "text";
  };
  custom_bot?: {
    enabled: boolean;
    token: string;
    status: "online" | "offline" | "error";
    nickname?: string;
    status_text?: string;
  };
  premium?: boolean;
  embed_builder?: {
    enabled: boolean;
    embeds: Array<{
      id: string;
      name: string;
      channel_id?: string;
      webhook_url?: string;
      message_id?: string;
      content?: string;
      embed?: {
        title?: string;
        description?: string;
        url?: string;
        color?: string;
        author_name?: string;
        author_icon_url?: string;
        author_url?: string;
        image_url?: string;
        thumbnail_url?: string;
        footer_text?: string;
        footer_icon_url?: string;
        fields?: Array<{ name: string; value: string; inline: boolean }>;
      };
    }>;
  };
  partnership?: {
    enabled: boolean;
    partnership_channel_id: string;
    manager_role_ids: string[];
    partner_role_id: string;
    minimum_members: number;
    reward_coins: number;
    partners: Array<{
      guild_id: string;
      name: string;
      invite_url: string;
      banner_url?: string;
      description?: string;
      partnered_at?: number;
      representative_id?: string;
    }>;
  };
  invite_tracker?: {
    enabled: boolean;
    log_channel_id: string;
    join_message?: string;
    leave_message?: string;
    fake_threshold_days?: number;
  };
  sticky_roles?: {
    enabled: boolean;
    role_ids?: string[];
  };
}

export interface PremiumPromotion {
  id: string;
  code: string;
  note: string;
  percent_off: number;
  plan_ids: string[];
  valid_from: number;
  valid_until: number;
  max_uses: number;
  uses: number;
  enabled: boolean;
  created_at: number;
}

/** Create / partial update (dates as YYYY-MM-DD). */
export interface PremiumPromotionCreate {
  code?: string;
  note?: string;
  percent_off?: number;
  plan_ids?: string[];
  valid_from?: string;
  valid_until?: string;
  max_uses?: number;
  enabled?: boolean;
}

// ---------- Endpoints ----------

export interface ServerBackup {
  id: string;
  name: string | null;
  guild_id: string | null;
  created_at: number | null;
  icon_url: string | null;
  size_bytes: number;
}

export const api = {
  publicStats: () => request<PublicStats>("/api/public/stats"),
  me: () => request<CurrentUser | null>("/api/me"),
  guilds: () => request<{ guilds: Guild[] }>("/api/guilds"),
  server: (guildId: string) =>
    request<ServerPanelData>(`/api/server/${guildId}`),
  serverBackups: (guildId: string) =>
    request<{ backups: ServerBackup[]; dir_exists: boolean }>(
      `/api/server/${guildId}/backups`,
    ),
  saveServer: (guildId: string, config: GuildConfig) =>
    request<{ ok: true; config: GuildConfig }>(
      `/api/server/${guildId}/save`,
      {
        method: "POST",
        body: JSON.stringify(config),
      },
    ),
  refresh: (guildId: string) =>
    request<{ ok: true }>(`/api/server/${guildId}/refresh`, {
      method: "POST",
    }),
  createChannel: (guildId: string, name: string, parentId?: string) =>
    request<{ ok: true; id: string; name: string }>(
      `/api/server/${guildId}/create_channel`,
      {
        method: "POST",
        body: JSON.stringify({ name, parent_id: parentId || "" }),
      },
    ),
  createCategory: (guildId: string, name: string) =>
    request<{ ok: true; id: string; name: string }>(
      `/api/server/${guildId}/create_category`,
      {
        method: "POST",
        body: JSON.stringify({ name }),
      },
    ),
  createRole: (guildId: string, name: string, color?: number) =>
    request<{ ok: true; id: string; name: string }>(
      `/api/server/${guildId}/create_role`,
      {
        method: "POST",
        body: JSON.stringify({ name, color: color || 0 }),
      },
    ),
  repostPanel: (
    guildId: string,
    payload:
      | { section: "tickets" }
      | { section: "whitelist" }
      | { section: "rr"; panel_id: string },
  ) =>
    request<{ ok: true }>(`/api/server/${guildId}/repost_panel`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  sendCustomEmbed: (
    guildId: string,
    payload: {
      channel_id?: string;
      webhook_url?: string;
      message_id?: string;
      content?: string;
      embed?: {
        title?: string;
        description?: string;
        url?: string;
        color?: string;
        author_name?: string;
        author_icon_url?: string;
        author_url?: string;
        image_url?: string;
        thumbnail_url?: string;
        footer_text?: string;
        footer_icon_url?: string;
        fields?: Array<{ name: string; value: string; inline: boolean }>;
      };
    },
  ) =>
    request<{ ok: true; message_id?: string; via: "bot" | "webhook" }>(
      `/api/server/${guildId}/send_custom_embed`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    ),
  gamesGrantCoins: (guildId: string, body: { user_id: string; amount: number }) =>
    request<{
      ok: true;
      user_id: string;
      new_balance: number;
      granted: number;
    }>(`/api/server/${guildId}/games_grant`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  gamesTakeCoins: (guildId: string, body: { user_id: string; amount: number }) =>
    request<{
      ok: true;
      user_id: string;
      new_balance: number;
      removed: number;
    }>(`/api/server/${guildId}/games_take`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Premium
  premiumInfo: (guildId: string) =>
    request<{
      guild_id: string;
      premium: boolean;
      can_buy: boolean;
      reason?: string;
      plan?: string;
      expires_at?: number;
      payment_provider?: string;
      has_subscription?: boolean;
      paypal_configured?: boolean;
      currency?: string;
      prices?: Record<string, number>;
      allow_simulate?: boolean;
      manual_payment?: {
        enabled: boolean;
        contact?: string;
        methods: Array<{
          id: string;
          label: string;
          handle: string;
          holder?: string;
          url_template?: string;
          description?: string;
        }>;
      };
    }>(`/api/premium/info?guild_id=${guildId}`),
  requestManualPremium: (body: {
    guild_id: string;
    plan: string;
    payment_method: "paypal_me" | "revolut" | "iban" | "other";
    payment_reference: string;
    note?: string;
    promo_code?: string;
  }) =>
    request<{ ok: true; request_id: string; status: string; message: string }>(
      "/api/premium/request_manual",
      { method: "POST", body: JSON.stringify(body) },
    ),
  myPremiumRequests: () =>
    request<{
      requests: Array<{
        id: string;
        guild_id: string;
        plan: string;
        payment_method: string;
        payment_reference: string;
        status: "pending" | "approved" | "rejected";
        created_at: number;
        reject_reason?: string;
      }>;
    }>("/api/premium/my_requests"),
  adminPremiumRequests: (status: "pending" | "approved" | "rejected" | "all" = "pending") =>
    request<{
      requests: Array<{
        id: string;
        guild_id: string;
        user_id: string;
        user_name: string;
        plan: string;
        payment_method: string;
        payment_reference: string;
        note?: string;
        promo_code?: string;
        status: string;
        created_at: number;
        approved_at?: number;
        rejected_at?: number;
        reject_reason?: string;
      }>;
    }>(`/api/admin/premium_requests?status=${status}`),
  adminApproveRequest: (reqId: string) =>
    request<{ ok: true; message: string }>(
      `/api/admin/premium_requests/${reqId}/approve`,
      { method: "POST", body: "{}" },
    ),
  adminRejectRequest: (reqId: string, reason?: string) =>
    request<{ ok: true; message: string }>(
      `/api/admin/premium_requests/${reqId}/reject`,
      {
        method: "POST",
        body: JSON.stringify({ reason: reason || "" }),
      },
    ),
  adminDeleteRequest: (reqId: string) =>
    request<{ ok: true }>(
      `/api/admin/premium_requests/${reqId}`,
      { method: "DELETE" },
    ),
  activatePremium: (guildId: string, plan: string) =>
    request<{ ok: true; redirect: string }>("/api/premium/activate", {
      method: "POST",
      body: JSON.stringify({ guild_id: guildId, plan }),
    }),
  paypalCreateCheckout: (
    guildId: string,
    plan: string,
    promoCode?: string,
  ) =>
    request<{ ok: true; approve_url: string; order_id?: string; subscription_id?: string }>(
      "/api/premium/paypal/create_checkout",
      {
        method: "POST",
        body: JSON.stringify({
          guild_id: guildId,
          plan,
          ...(promoCode?.trim() ? { promo_code: promoCode.trim() } : {}),
        }),
      },
    ),
  cancelSubscription: (guildId: string) =>
    request<{ ok: true; message: string }>("/api/premium/cancel_subscription", {
      method: "POST",
      body: JSON.stringify({ guild_id: guildId }),
    }),
  simulatePremiumCheckout: (
    guildId: string,
    plan: string,
    promoCode?: string,
  ) =>
    request<{ ok: true; redirect: string }>("/api/premium/simulate_checkout", {
      method: "POST",
      body: JSON.stringify({
        guild_id: guildId,
        plan,
        ...(promoCode?.trim() ? { promo_code: promoCode.trim() } : {}),
      }),
    }),
  promoPreview: (code: string, plan: string) =>
    request<{ valid: boolean; percent_off: number; message: string }>(
      `/api/premium/promo_preview?${new URLSearchParams({ code, plan }).toString()}`,
    ),
  // Admin
  adminPromotions: () =>
    request<{ promotions: PremiumPromotion[] }>("/api/admin/promotions"),
  adminPromotionCreate: (body: PremiumPromotionCreate) =>
    request<{ ok: true; promotion: PremiumPromotion }>(
      "/api/admin/promotions",
      { method: "POST", body: JSON.stringify(body) },
    ),
  adminPromotionPatch: (id: string, body: Partial<PremiumPromotionCreate>) =>
    request<{ ok: true; promotion: PremiumPromotion }>(
      `/api/admin/promotions/${id}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),
  adminPromotionDelete: (id: string) =>
    request<{ ok: true }>(`/api/admin/promotions/${id}`, { method: "DELETE" }),
  adminPremium: () =>
    request<{ 
      guilds: Record<string, { plan: string; expires_at: number; granted_by: string; granted_at: number }>;
      users: Record<string, { plan: string; expires_at: number; granted_by: string; granted_at: number }>;
    }>(
      "/api/admin/premium",
    ),
  grantPremium: (targetId: string, plan: string, days: number, isUser: boolean = false) =>
    request<{ ok: true }>("/api/admin/grant_premium", {
      method: "POST",
      body: JSON.stringify({ target_id: targetId, plan, days, is_user: isUser }),
    }),
  revokePremium: (targetId: string, isUser: boolean = false) =>
    request<{ ok: true }>("/api/admin/revoke_premium", {
      method: "POST",
      body: JSON.stringify({ target_id: targetId, is_user: isUser }),
    }),
  adminStats: () =>
    request<{
      total_guilds_configured: number;
      bot_guild_count: number;
      total_economy_users: number;
      total_premium_guilds: number;
      total_premium_users: number;
      node_version: string;
      platform: string;
      uptime: number;
    }>("/api/admin/stats"),
  adminGuilds: () => request<{ guilds: Guild[] }>("/api/admin/guilds"),
  adminDiagnostics: () =>
    request<{
      ok: boolean;
      results: Array<{ name: string; ok: boolean; message: string }>;
    }>("/api/admin/diagnostics"),
  // AI Architect
  aiArchitectPlan: (guildId: string, prompt: string) =>
    request<{ ok: boolean; plan: any; error?: string }>(
      `/api/server/${guildId}/ai-architect/plan`,
      {
        method: "POST",
        body: JSON.stringify({ prompt }),
      }
    ),
  aiArchitectInitDiscord: (guildId: string, body: { prompt: string; total: number; channelId: string }) =>
    request<{ ok: boolean; messageId: string; error?: string }>(
      `/api/server/${guildId}/ai-architect/init-discord`,
      {
        method: "POST",
        body: JSON.stringify(body),
      }
    ),
  aiArchitectUpdateDiscord: (guildId: string, body: { messageId: string; channelId: string; status: string; current: number; total: number; prompt: string }) =>
    request<{ ok: boolean; error?: string }>(
      `/api/server/${guildId}/ai-architect/update-discord`,
      {
        method: "POST",
        body: JSON.stringify(body),
      }
    ),
  aiArchitectBuildItem: (guildId: string, body: { type: string; item: any }) =>
    request<{ ok: boolean; data: any; error?: string }>(
      `/api/server/${guildId}/ai-architect/build-item`,
      {
        method: "POST",
        body: JSON.stringify(body),
      }
    ),
  adminRefreshGuilds: () =>
    request<{ ok: true; count: number }>("/api/admin/refresh_guilds", {
      method: "POST",
    }),
  adminLogs: () => request<{ logs: any[] }>("/api/admin/logs"),
  // Partnership Management
  getPartners: (guildId: string) =>
    request<{ ok: boolean; partnership: any }>(`/api/server/${guildId}/partners`),
  updatePartnershipSettings: (guildId: string, settings: any) =>
    request<{ ok: boolean; partnership: any }>(`/api/server/${guildId}/partners/settings`, {
      method: "POST",
      body: JSON.stringify(settings),
    }),
  addPartner: (guildId: string, partner: any) =>
    request<{ ok: boolean; partner: any }>(`/api/server/${guildId}/partners`, {
      method: "POST",
      body: JSON.stringify(partner),
    }),
  deletePartner: (guildId: string, partnerId: string) =>
    request<{ ok: boolean }>(`/api/server/${guildId}/partners/${partnerId}`, {
      method: "DELETE",
    }),
  // Game Alerts & Promos (PatchBot Style)
  getGameAlerts: (guildId: string) =>
    request<{ ok: boolean; game_alerts: any }>(`/api/server/${guildId}/game-alerts`),
  updateGameAlertsSettings: (guildId: string, settings: any) =>
    request<{ ok: boolean; game_alerts: any }>(`/api/server/${guildId}/game-alerts/settings`, {
      method: "POST",
      body: JSON.stringify(settings),
    }),
  // Invite Tracker
  getInvitesLeaderboard: (guildId: string) =>
    request<{
      ok: boolean;
      users: Array<{
        id: string;
        username: string;
        avatar?: string;
        regular: number;
        fake: number;
        leave: number;
        bonus: number;
        total: number;
      }>;
    }>(`/api/server/${guildId}/invites/leaderboard`),
  adjustInvites: (
    guildId: string,
    body: {
      user_id: string;
      amount: number;
      operation: "add" | "remove";
    }
  ) =>
    request<{
      ok: boolean;
      user_id: string;
      new_bonus: number;
    }>(`/api/server/${guildId}/invites/adjust`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
